import { memo, useMemo } from "react";

import type {
	PlainSettingForDesignPlainQuery,
	PlainWidgetForDesignPlainQuery,
	SnackListForDesignPlainQuery,
} from "@/lib/gql/__generated__/graphql";
import { LangCode, PlainWidgetDecoration } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandContainer, FeatureIslandHeader, FeatureIslandMain } from "@/components/features/_layouts";

import { MainForm, ReservationPane, SaveButton } from "./components";
import { useInitialized } from "../model/use-case";

type Props = {
	hotelID: string;
	plainSettingForDesignPlainServerModel: PlainSettingForDesignPlainQuery;
	snackListServerModel: SnackListForDesignPlainQuery;
	plainWidgetDefaultOfServerModel: PlainWidgetForDesignPlainQuery;

	/** 最初の登録が完了しているかどうか */
	isFirstRegistrationCompleted: boolean;
};

export const DesignPlainPresenter = memo(
	({
		hotelID,
		isFirstRegistrationCompleted,
		plainSettingForDesignPlainServerModel,
		snackListServerModel,
		plainWidgetDefaultOfServerModel,
	}: Props) => {
		const {
			// read
			queryData,
			viewModel,

			// mutation
			onUpdatePlainSettingAndWidget,

			// urlSettingForm
			onChangeDomain,

			// basicSettingForm
			// color
			onChangeColorBackground,
			onChangeColorPrimary,
			onChangeColorFont,
			onApplyRecommendationColors,
			// hotelInformation
			onChangeLogo,
			onChangeLogoSize,
			onChangeIsShowHotelName,
			onChangeIsShowHotelCatchCopy,
			onChangeIsShowHotelLogo,
			onChangeIsShowHotelKeyVisual,
			// fontFamily
			onChangeFontFamily,
			// reservationComponent
			onChangeReservationComponentType,

			// contentsSettingForm
			// snack
			onAddPlainWidgetDefault,
			onRemovePlainWidgetDefault,
			onChangeWidgetDecoration,
			onUpdateOrderPlainWidgets,
			// magazine
			onChangeIsShowMagazineAbout,
			onChangeIsShowMagazineRoom,
			onChangeIsShowMagazinePlan,

			// analysisSettingForm
			onChangeGtmID,

			// guestPageVisibility
			onChangeGuestPageUnlisted,
		} = useInitialized({
			plainSettingForDesignPlainServerModel,
			plainWidgetDefaultOfServerModel,
			snackListServerModel,
		});

		/**
		 * 公開済みの snack のみを抽出
		 */
		const activeSnacks = useMemo(
			() => queryData.snackList.snacks.filter((snack) => snack.isPublished),
			[queryData.snackList.snacks],
		);

		const reservationPaneElement = (
			<ReservationPane
				colorBackground={viewModel.colorBackground.value}
				primaryColor={viewModel.colorPrimary.value}
				fontColor={viewModel.colorFont.value}
				plainFontFamily={viewModel.fontFamily}
				// cover
				mainKeyVisual={queryData.hotel.mainKeyVisual ?? undefined}
				logoImage={viewModel.logoImage ?? undefined}
				logoSize={viewModel.logoSize}
				isShowHotelKeyVisual={viewModel.isShowHotelKeyVisual}
				isShowHotelLogo={viewModel.isShowHotelLogo}
				// about
				hotelName={queryData.hotel.name || ""}
				hotelCatchCopy={queryData.hotel.catchCopy || ""}
				isShowHotelName={viewModel.isShowHotelName}
				isShowHotelCatchCopy={viewModel.isShowHotelCatchCopy}
				// reservation
				reservationComponentType={viewModel.reservationComponentType}
				widgetDecoration={viewModel.widgetDecoration ?? PlainWidgetDecoration.None}
				// snack
				snacks={activeSnacks}
				plainWidgetItems={viewModel.plainWidgetDefault.widgetItems}
				// magazine
				rooms={queryData.roomList.rooms}
				plans={queryData.planList.plans}
				isShowMagazineAbout={viewModel.isShowMagazineAbout ?? false}
				isShowMagazinePlan={viewModel.isShowMagazinePlan ?? false}
				isShowMagazineRoom={viewModel.isShowMagazineRoom ?? false}
				//
				lang={LangCode.Default}
			/>
		);

		return (
			<FeatureIslandContainer>
				<FeatureIslandHeader
					title="プレーンの設定"
					rightChildren={
						<SaveButton
							hasError={viewModel.hasError}
							isDirty={viewModel.isDirty}
							onClickUpdatePlainSettingAndWidget={onUpdatePlainSettingAndWidget}
						/>
					}
				/>

				<FeatureIslandMain>
					<div className="relative flex gap-x-6">
						<MainForm
							hotelID={hotelID}
							isFirstRegistrationCompleted={isFirstRegistrationCompleted}
							mobilePreview={reservationPaneElement}
							// urlSettingForm
							domainName={viewModel.domainName}
							subDomainName={viewModel.subDomainName.value}
							subDomainNameErrorMessage={viewModel.subDomainName.error}
							onChangeDomain={onChangeDomain}
							// guestPageVisibility
							guestPageVisibility={viewModel.guestPageVisibility}
							onChangeGuestPageUnlisted={onChangeGuestPageUnlisted}
							// basicSettingForm
							// color
							backgroundColor={viewModel.colorBackground}
							onChangeColorBackground={onChangeColorBackground}
							primaryColor={viewModel.colorPrimary}
							onChangeColorPrimary={onChangeColorPrimary}
							fontColor={viewModel.colorFont}
							onChangeColorFont={onChangeColorFont}
							onApplyRecommendationColors={onApplyRecommendationColors}
							// hotelInformation
							logoImage={viewModel.logoImage ?? undefined}
							logoSize={viewModel.logoSize}
							isShowHotelName={viewModel.isShowHotelName}
							isShowHotelCatchCopy={viewModel.isShowHotelCatchCopy}
							isShowHotelLogo={viewModel.isShowHotelLogo}
							isShowHotelKeyVisual={viewModel.isShowHotelKeyVisual}
							onChangeLogo={onChangeLogo}
							onChangeLogoSize={onChangeLogoSize}
							onChangeIsShowHotelName={onChangeIsShowHotelName}
							onChangeIsShowHotelCatchCopy={onChangeIsShowHotelCatchCopy}
							onChangeIsShowHotelLogo={onChangeIsShowHotelLogo}
							onChangeIsShowHotelKeyVisual={onChangeIsShowHotelKeyVisual}
							// fontFamily
							plainFontFamily={viewModel.fontFamily}
							onChangeFontFamily={onChangeFontFamily}
							// reservationComponent
							reservationComponentType={viewModel.reservationComponentType}
							onChangeReservationComponentType={onChangeReservationComponentType}
							// contentsSettingForm
							// snack
							widgetDecoration={viewModel.widgetDecoration}
							snacks={activeSnacks}
							plainWidgetItems={viewModel.plainWidgetDefault.widgetItems}
							onAddPlainWidgetDefault={onAddPlainWidgetDefault}
							onRemovePlainWidgetDefault={onRemovePlainWidgetDefault}
							onChangeWidgetDecoration={onChangeWidgetDecoration}
							onUpdateOrderPlainWidgets={onUpdateOrderPlainWidgets}
							// magazine
							isShowMagazineAbout={viewModel.isShowMagazineAbout ?? false}
							isShowMagazinePlan={viewModel.isShowMagazinePlan ?? false}
							isShowMagazineRoom={viewModel.isShowMagazineRoom ?? false}
							onChangeIsShowMagazineAbout={onChangeIsShowMagazineAbout}
							onChangeIsShowMagazinePlan={onChangeIsShowMagazinePlan}
							onChangeIsShowMagazineRoom={onChangeIsShowMagazineRoom}
							// analysisSettingForm
							gtmId={viewModel.gtmID?.value ?? undefined}
							gtmIdErrorMessage={viewModel.gtmID?.error}
							onChangeGtmID={onChangeGtmID}
						/>

						<div className="overflow sticky top-24 hidden max-h-[calc(100vh_-_180px)] w-[420px] min-w-[420px] max-w-[420px] overflow-y-scroll rounded-xl laptop:block">
							{reservationPaneElement}
						</div>
					</div>
				</FeatureIslandMain>
			</FeatureIslandContainer>
		);
	},
);
